import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccounts } from "@/hooks/useAccounts";
import { useCredit } from "@/hooks/useCredit";
import { useEventEngine } from "@/hooks/useEventEngine";

import AccountCard from "@/components/financial/AccountCard";
import NetWorthSummary from "@/components/financial/NetWorthSummary";
import TransferModal from "@/components/financial/TransferModal";
import TransactionFeed from "@/components/financial/TransactionFeed";
import CreditAccountCard from "@/components/financial/CreditAccountCard";
import CreditScoreWidget from "@/components/financial/CreditScoreWidget";
import CreditPaymentModal from "@/components/financial/CreditPaymentModal";
import AutopaySetupModal from "@/components/financial/AutopaySetupModal";
import AutopayStatusCard from "@/components/financial/AutopayStatusCard";
import IncomeStreamsCard from "@/components/financial/IncomeStreamsCard";
import HeldEarningsCard from "@/components/financial/HeldEarningsCard";

import MonthlyExpensesSummary from "@/components/financial/MonthlyExpensesSummary";
import CareerPanel from "@/components/career/CareerPanel";
import IncomeOverviewCard from "@/components/payroll/IncomeOverviewCard";
import OtherIncomeCard from "@/components/payroll/OtherIncomeCard";

import { Wallet } from "lucide-react";
import { toast } from "sonner";
import PageShell from "@/components/premium/PageShell";
import PageHeader from "@/components/premium/PageHeader";
import SectionHeader from "@/components/premium/SectionHeader";
import { getErrorMessage } from "@/lib/errors";

const FinancialDashboard = () => {
  const {
    accounts,
    transactions,
    loading,
    netWorth,
    liquidCash,
    reserves,
    retirement,
    investments,
    transfer,
    refetch: refetchAccounts,
  } = useAccounts();
  const {
    credit,
    isLocked,
    availableCredit,
    utilization,
    scoreTier,
    makePayment,
    unlock,
    configureAutopay,
    loading: creditLoading,
  } = useCredit();
  const { incomeStreams, multiplier } = useEventEngine();
  
  const navigate = useNavigate();
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferFrom, setTransferFrom] = useState("checking");
  const [creditPayOpen, setCreditPayOpen] = useState(false);
  const [autopayOpen, setAutopayOpen] = useState(false);

  const openTransfer = (fromType: string = "checking") => {
    setTransferFrom(fromType);
    setTransferOpen(true);
  };

  const handleCreditPayment = async (amount: number) => {
    await makePayment(amount);
    await refetchAccounts();
  };

  const handleConfigureAutopay = async (
    enabled: boolean,
    sourceAccountType: string,
    amountType: string,
    customAmount?: number
  ) => {
    await configureAutopay(enabled, sourceAccountType, amountType, customAmount);
    await refetchAccounts();
  };

  const handleDisableAutopay = async () => {
    try {
      await configureAutopay(false);
      toast.success("Autopay disabled");
    } catch (e: unknown) {
      toast.error(getErrorMessage(e) || "Failed to disable autopay");
    }
  };

  const checkingBalance = accounts.find((account) => account.account_type === "checking")?.balance || 0;

  // Find source account name for autopay display
  const autopaySourceName = credit?.autopay_source_account_id
    ? accounts.find((a) => a.id === credit.autopay_source_account_id)?.account_name || null
    : null;

  if (loading || creditLoading) {
    return (
      <PageShell>
        <div className="flex min-h-[40vh] items-center justify-center">
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <p className="font-medium text-muted-foreground">Loading accounts...</p>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title="Finance Center"
        subtitle="Practice real-world money decisions in a safe simulation."
        icon={Wallet}
      />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <NetWorthSummary
            netWorth={netWorth}
            liquidCash={liquidCash}
            reserves={reserves}
            retirement={retirement}
            investments={investments}
          />
          <CreditScoreWidget score={credit?.credit_score || 650} tier={scoreTier} isLocked={isLocked} />
        </div>

        <HeldEarningsCard />
        <IncomeOverviewCard />

        <OtherIncomeCard />
        <IncomeStreamsCard streams={incomeStreams} multiplier={multiplier} />
        <div>
          <SectionHeader title="Career & Paychecks" description="Your activated career, daily check breakdown, and side hustle metrics." />
          <CareerPanel />
        </div>
        <MonthlyExpensesSummary />

        <div>
          <SectionHeader title="Your Accounts" description="Manage your simulated balances and transfers." />
          <div className="flex flex-col gap-3 sm:grid sm:grid-cols-2">
            {accounts.map((account) => (
              <AccountCard
                key={account.id}
                account={account}
                onTransfer={account.status === "active" ? () => openTransfer(account.account_type) : undefined}
              />
            ))}
            <CreditAccountCard
              credit={credit}
              isLocked={isLocked}
              availableCredit={availableCredit}
              utilization={utilization}
              onMakePayment={() => setCreditPayOpen(true)}
              onUnlock={unlock}
            />
          </div>
        </div>

        {/* Autopay Section */}
        {credit && !isLocked && (
          <div>
            <SectionHeader title="Autopay" description="Automate credit card payments from unknown account." />
            <AutopayStatusCard
              credit={credit}
              sourceAccountName={autopaySourceName}
              onSetup={() => setAutopayOpen(true)}
              onDisable={handleDisableAutopay}
            />
          </div>
        )}

        <div>
          <SectionHeader title="Recent Activity" description="Latest transactions and money events." />
          <TransactionFeed transactions={transactions} />
        </div>

      <TransferModal
        open={transferOpen}
        onOpenChange={setTransferOpen}
        accounts={accounts}
        onTransfer={transfer}
        preselectedFrom={transferFrom}
      />

      <CreditPaymentModal
        open={creditPayOpen}
        onOpenChange={setCreditPayOpen}
        currentBalance={credit?.current_balance || 0}
        minimumPayment={credit?.minimum_payment || 0}
        checkingBalance={checkingBalance}
        onPay={handleCreditPayment}
      />

      <AutopaySetupModal
        open={autopayOpen}
        onOpenChange={setAutopayOpen}
        accounts={accounts}
        currentBalance={credit?.current_balance || 0}
        minimumPayment={credit?.minimum_payment || 0}
        nextDueDate={credit?.payment_due_date || null}
        onConfigure={handleConfigureAutopay}
      />
    </PageShell>
  );
};

export default FinancialDashboard;
